import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injector } from "@angular/core";
import * as kendoDrawing from '@progress/kendo-drawing';
import { Group } from "@progress/kendo-drawing";
import { PDFOptions } from "@progress/kendo-drawing/dist/npm/pdf";
import { lastValueFrom } from "rxjs";
import { environment } from "../../../../../environments/environment";
import { DestroyBase } from "../../../../components/common/ng-destroy-base";
import { AsyncLock } from "../../../../components/common/utils/async-lock";
import { Queue } from "../../../../components/common/utils/queue";
import { GenericPolluxResult } from "../../../../models/generic-pollux-result";
import { AppServiceHelperService } from "../../../../services/app-service-helper.service";
import { EventBusService } from "../../../../services/event-bus.service";
import { FileDownloadService } from "../../../../services/file-download-service.service";
import { GuidService } from "../../../project-valuation/services/guid.service";
import { ReportDesignerDocumentComponent } from "../../components/report-designer-document/report-designer-document.component";
import { ReportDesignerPageComponent } from "../../components/report-designer-page/report-designer-page.component";
import { HashtagService } from "../../hashtags/services/hashtag.service";
import { ReportDesignerPage } from "../../models/report-designer-page";
import { ReportDesignerPrintSettings } from "../../models/report-designer-print-settings";
import { ReportDesignerHostComponent } from "../../report-designer-host/report-designer-host.component";
import { WidgetService } from "../../widgets/widget.service";
import { ReportDesignerStorageService } from "../report-designer-storage.service";
import { PausePrintRequest } from "./models/pause-print-request";
import { UploadedPageInfo } from "./models/uploaded-page-info";
import { ReportDataPreloaderService } from "./report-data-preloader-service";

/**
 * Print service base class
 */
export abstract class PrintServiceBase extends DestroyBase {
  /**
   * Page waits for print?
   */
  abstract readonly pageWaitsForPrint: boolean;

  /**
   * Raise CSS print events?
   */
  abstract readonly raiseCssPrintEvents: boolean;

  /**
   * ReportDesignerStorageService
   */
  protected rdStorage: ReportDesignerStorageService;

  /**
   * HttpClient
   */
  protected http: HttpClient;

  /**
   * AppServiceHelperService
   */
  protected appServiceHelper: AppServiceHelperService;

  /**
   * GuidService
   */
  protected guidService: GuidService;

  /**
   * FileDownloadService
   */
  protected fileDownloadService: FileDownloadService;

  /**
   * EventBusService
   */
  protected eventBus: EventBusService

  /**
   * ReportDataPreloaderService
   */
  protected reportDataPreloaderService: ReportDataPreloaderService;

  /**
   * HashtagService
   */
  protected hashtagService: HashtagService;

  /**
   * WidgetService
   */
  protected widgetService: WidgetService;

  /**
   * The RD host component
   */
  protected host: ReportDesignerHostComponent;

  /**
   * The RD document
   */
  protected document: ReportDesignerDocumentComponent;

  /**
   * The RD pages
   */
  protected pages: ReportDesignerPage[];

  /**
   * The print process GUID
   */
  protected printGuid: string;

  /**
   * Document data loaded?
   */
  protected documentDataLoadedProcessed = false;

  /**
   * Timeout after loading
   */
  protected PROCESS_AFTER_LOADED_TIMEOUT = 200;

  /**
   * Page stabilized timeout: Give Angular some time
   */
  protected PAGE_STABILIZED_TIMEOUT = 1000;

  /**
   * No mutations timeout
   */
  protected NO_MUTATIONS_TIMEOUT = 100;

  /**
   * Default no mutations timeout
   */
  protected DEFAULT_NO_MUTATIONS_TIMEOUT = 300;

  /**
   * Use a mutation observer?
   */
  protected useMutationObserver = true;

  /**
   * Pages reqdy to be processed
   */
  protected readyToProcessQueue = new Queue<ReportDesignerPageComponent>();

  /**
   * The processing queue
   */
  protected processingQueue = new Queue<ReportDesignerPageComponent>();

  /**
   * Indexes of processed pages
   */
  protected processedPagesIndexes: number[] = [];

  /**
   * Uploaded pages
   */
  protected uploadedPages: UploadedPageInfo[] = [];

  /**
   * Error occurred?
   */
  protected errorOccurred = false;

  /**
   * Set to true just for development
   */
  protected fakeUpload = false;

  /**
   * Lock for printing
   */
  private readonly kendoPrintLock = new AsyncLock();

  /**
   * Merge in progress?
   */

  protected mergeInProgress = false;

  /**
   * Pause requested?
   */
  protected pauseRequested = false;

  /**
   * The window
   */
  protected get _window(): any {
    return this.window;
  }

  /**
   * Log messages in DEV environment
   * @param message
   * @param prependMessage
   * @param appendMessage
   * @param color
   */
  log(message: string, prependMessage = '', appendMessage = '', color = '') {
    if (!environment.production) {
      console.log(`%c${prependMessage}PagedPrintService: ${message}${appendMessage}`, color);
    }
  }

  /**
   * Constructor
   * @param printSettings ReportDesignerPrintSettings
   * @param injector Injector
   * @param window Window
   */
  constructor(protected printSettings: ReportDesignerPrintSettings, protected injector: Injector, protected window: Window) {
    super();
    this.rdStorage = injector.get(ReportDesignerStorageService);
    this.http = injector.get(HttpClient);
    this.appServiceHelper = injector.get(AppServiceHelperService);
    this.guidService = injector.get(GuidService);
    this.fileDownloadService = injector.get(FileDownloadService);
    this.eventBus = injector.get(EventBusService);
    this.reportDataPreloaderService = injector.get(ReportDataPreloaderService);
    this.hashtagService = injector.get(HashtagService);
    this.widgetService = injector.get(WidgetService);

    this.printGuid = this.guidService.generate();

    //function can be called from puppeteer if some error occurs
    this._window.printErrorOccurred = this.markError.bind(this);
  }

  /**
   * Initialization logic
   * @param host
   */
  abstract init(host: ReportDesignerHostComponent): void;

  /**
   * Process the queue
   */
  protected abstract processQueue(): void;

  /**
   * Batch pages for printing
   * @param processedPageComponent
   */
  protected abstract batchPagesForPrint(processedPageComponent: ReportDesignerPageComponent): void;

  /**
   * Gets the printable pages
   * @returns
   */
  protected getPrintablePages(): ReportDesignerPage[] {
    const pages = this.pages;
    const printSettings = this.printSettings;

    return pages.filter((p) => {
      //Skip page if hidden
      let shouldSkipPage = p.options.isHidden;
      //Skip the cover page if the checkbox isn't checked
      shouldSkipPage ||= p.options.isCoverPage && !printSettings.coverPage;
      //Skip the back cover page if the checkbox isn't checked
      shouldSkipPage ||= p.options.isBackCoverPage && !printSettings.backCoverPage;

      return !shouldSkipPage;
    });
  }

  /**
   * Gets the page's index
   * @param page
   * @returns
   */
  protected getPageIndex(page: ReportDesignerPage): number {
    return this.getPrintablePages().indexOf(page);
  }

  /**
   * Gets the next page after a page
   * @param page
   * @returns
   */
  protected getNextPage(page: ReportDesignerPage): ReportDesignerPage | null {
    const pages = this.getPrintablePages();
    const currentPage = pages.find(p => p === page);
    const nextPageIndex = this.pages.indexOf(currentPage) + 1;
    return nextPageIndex < pages.length ? pages[nextPageIndex] : null;
  }

  /**
   * Prepare the document for printing
   */
  protected prepareDocumentForPrint() {
    if (this.printSettings.hidePageNumbers) {
      this.host.addDocumentClass("reportPageNumbersHidden");
    }

    if (!this.printSettings.includeHeader) {
      this.host.addDocumentClass("reportHeaderHidden");
    }

    if (!this.printSettings.includeFooter) {
      this.host.addDocumentClass("reportFooterHidden");
    }

    this.host.removeDocumentClass("ReportOverviewMode");

    //for paged print we could end up with more pages (virtual pages)..but this is just info message/event
    const numberOfPages = this.getPrintablePages().length;

    this.raisePrintEvent("pdf-export-pages_" + numberOfPages, `Pages: ${numberOfPages}`);
    this.raisePrintEvent("pdf-export-pages");

    this.preparePagesForPrint();
  }

  /**
   * Prepare page sizes for printing
   */
  protected preparePagesForPrint(): void {
    const settings = this.printSettings;

    for (const page of this.pages) {
      page.options.isOnDisplayBeforePrint = page.options.isOnDisplay;
      page.options.isHiddenBeforePrint = page.options.isHidden;

      page.options.isOnDisplay = true;

      if (page.options.isCoverPage) {
        if (!settings.hideCoverPage) {
          page.options.isHidden = false;
          page.options.isOnDisplay = true;
        }
        else {
          page.options.isHidden = true;
          page.options.isOnDisplay = false;
        }
      }
      if (page.options.isBackCoverPage) {
        if (!settings.hideBackCoverPage) {
          page.options.isHidden = false;
          page.options.isOnDisplay = true;
        }
        else {
          page.options.isHidden = true;
          page.options.isOnDisplay = false;
        }
      }
    }
  }

  /**
   * Wait for the page to be ready to be printed
   * @param pageComponent
   * @returns
   */
  protected waitForReadyToPrint(pageComponent: ReportDesignerPageComponent): Promise<void> {

    /*
    TODO(BB): also check if there are still hashtags on page
      const hashtagsOnPageExists = pageComponent.elementRef.nativeElement.innerText.match(/#=\w+=#/g).length > 0;
    */

    return new Promise((resolve) => {
      const pageNumber = this.getPageIndex(pageComponent.page) + 1;

      if (!this.useMutationObserver) {
        setTimeout(() => {
          resolve();
        }, this.PAGE_STABILIZED_TIMEOUT);
      } else {

        //fallback timeout waits for PAGE_STABILIZED_TIMEOUT, might be canceled in mutation observer or default timer
        const fallbackId = setTimeout(() => {
          this.log(`Mutation keep runing after ${this.PAGE_STABILIZED_TIMEOUT}ms.. Start print page ${pageNumber}`, '\n', '\n\n', 'color: green');
          clearTimeout(timerId);
          observer.disconnect();
          resolve();
        }, this.PAGE_STABILIZED_TIMEOUT);

        //default timeout waits for DEFAULT_NO_MUTATIONS_TIMEOUT, might be canceled in mutation observer
        let timerId = setTimeout(() => {
          this.log(`No mutation occurred after ${this.DEFAULT_NO_MUTATIONS_TIMEOUT}ms.. Start print page ${pageNumber}`, '\n', '\n\n', 'color: green');
          clearTimeout(fallbackId);
          observer.disconnect();
          resolve();
        }, this.DEFAULT_NO_MUTATIONS_TIMEOUT);

        const observer = new MutationObserver(mutationRecords => {
          this.log(`Mutations: ${mutationRecords.length}.. wait for print`);
          //cancel timer and start again
          clearTimeout(timerId);
          //start printing page 100ms (NO_MUTATIONS_TIMEOUT) after no more mutations on page
          timerId = setTimeout(() => {
            this.log(`No more mutation occurred in ${this.NO_MUTATIONS_TIMEOUT}ms.. Start print page ${pageNumber}`, '\n', '\n\n', 'color: green');
            clearTimeout(fallbackId);
            observer.disconnect();
            resolve();
          }, this.NO_MUTATIONS_TIMEOUT);
        });

        observer.observe(pageComponent.elementRef.nativeElement, {
          childList: true, // observe direct children
          subtree: true, // and lower descendants too
          attributes: true // observe attributes
        });
      }
    });
  }

  /**
   * Prints a page
   * @param pageComponent
   */
  protected printPage(pageComponent: ReportDesignerPageComponent): void {
    this.window.requestAnimationFrame(() => {
      //first frame could be before reflow, so once more
      this.window.requestAnimationFrame(() => {
        const pageCounter = this.getPageIndex(pageComponent.page) + 1;
        this.raisePrintEvent(`pdf-export-print-page_${pageCounter}`, `Exporting page: ${pageCounter}`);

        const containerEl = pageComponent.elementRef.nativeElement as HTMLElement;
        const pageEl: HTMLElement = containerEl.querySelector('.pdf-page');

        this.kendoPageToPdf(pageEl).then((pageBase64: string) => {
          if (!this.pauseRequested) {
            this.sendPagePdfToServer(pageBase64, pageComponent);
            this.processedPagesIndexes.push(this.getPageIndex(pageComponent.page));
            //when page pdf is sent to server we can continue with processing other pages
            this.processNextPage(pageComponent);
          }
        }, () => {
          this.handlePrintError();
        });
      });
    });
  }

  /**
   * PROCESS THE NEXT PAGE
   * @param pageComponent
   */
  protected processNextPage(pageComponent: ReportDesignerPageComponent): void {
    //remove processed page
    pageComponent.page.options.waitingForPrint = true;

    //remove from queue
    this.processingQueue.dequeueItem(pageComponent);

    //and start loading next one(s)
    this.batchPagesForPrint(pageComponent);

    //process other pages that might be in queue (virtual pages)
    this.processQueue();
  }

  /**
   * Print a page to PDF using Kendo
   * @param pageEl
   * @returns
   */
  protected async kendoPageToPdf(pageEl: HTMLElement): Promise<string> {
    await this.kendoPrintLock.acquire();
    try {
      kendoDrawing.pdf.defineFont({
        "Arial": "assets/fonts/arial.ttf", // this is a URL
        "Arial|Bold": "assets/fonts/arialbd.ttf",
        "Arial|Bold|Italic": "assets/fonts/arialbi.ttf",
        "Arial|Italic": "assets/fonts/ariali.ttf"
      });

      const drawDOMresult: Group = await kendoDrawing.drawDOM(pageEl);

      const options: PDFOptions = {
        multiPage: true
      };

      const pageBase64: string = await kendoDrawing.exportPDF(drawDOMresult, options)

      return pageBase64;
    } finally {
      this.kendoPrintLock.release();
    }
  }

  /**
   * Upload a finished page PDF to the server
   * @param pageBase64
   * @param pageComponent
   */
  protected sendPagePdfToServer(pageBase64: string, pageComponent: ReportDesignerPageComponent): void {
    pageBase64 = String(pageBase64).replace('data:application/pdf;base64,', '');
    const pageIndex = this.getPageIndex(pageComponent.page);
    //upload page to server
    const data = {
      DocumentBase64: pageBase64,
      PageNo: pageIndex,
      PageSizeId: this.document.data.pageSizeId,
      PrintGuid: this.printGuid,
      PdfExportId: this.host.PdfExportId || null
    };

    const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportprint/pdfupload');
    const headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json; charset=utf-8');

    const page = pageComponent.page;
    const uploadInfo: UploadedPageInfo = {
      pageIndex,
      isVirtualPageMaster: !!page.isVirtualPageMaster,//force boolean
      isVirtualPage: !!page.isVirtualPage//force boolean
    }

    if (this.fakeUpload) {
      setTimeout(() => {
        this.log("Send FAKE page to server..");
        this.uploadedPages.push(uploadInfo);
      }, 100);
    } else {
      this.log("Send page to server..");
      this.http.post(url, data, { headers: headers }).subscribe({
        next: () => {
          if (!this.pauseRequested) {
            this.uploadedPages.push(uploadInfo);
          }
        },
        error: () => {
          this.handlePrintError();
        }
      });
    }
  }

  /**
   * Called when all page PDFs are created
   */
  protected pdfCreated(): void {
    //Restore the pages' visibility
    for (const page of this.pages) {
      page.options.isOnDisplay = page.options.isOnDisplayBeforePrint;
      page.options.isHidden = page.options.isHiddenBeforePrint;
      delete page.options.isOnDisplayBeforePrint;
      delete page.options.isHiddenBeforePrint;
    }

    if (this.rdStorage.getAppPrintMode()) {
      this.raisePrintEvent('pdf-export-merging', 'Merging');
    }

    this.host.setPrintProgress('publish.merge', 1, 0, false);

    if (!this.fakeUpload) {
      //merge files
      const data = {
        ReportId: this.rdStorage.getReport(),
        PrintGuid: this.printGuid,
        KeepFiles: this.printSettings.serverSidePrint,
        settings: this.printSettings,
        PdfExportId: null
      };

      if (!this.printSettings.serverSidePrint) {
        const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportprint/merge');

        //if started by user (UI) => download file
        this.fileDownloadService.initiateDownloadPost(url, data).then(() => {
          this.pdfExportEnded();
        });
      } else {
        this.mergeInProgress = true;
        //in publish scenario (puppeteer), just merge (and do other server side stuff)
        data.PdfExportId = this.host.PdfExportId
        const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportprint/merge/publish');

        this.http.post(url, data).subscribe(() => {
          this.pdfExportEnded();
        });
      }
    } else {
      this.pdfExportEnded();
    }
  }

  /**
   * Called once the PDF export ended
   */
  protected pdfExportEnded(): void {
    this.puppeteerExportEnded();

    this.host.removeDocumentClass("print-in-progress");
    this.host.removeDocumentClass("reportPageNumbersHidden");
    this.host.removeDocumentClass("reportHeaderHidden");
    this.host.removeDocumentClass("reportFooterHidden");

    this.host.pdfExportFinished();
    this.host.setPrintProgress('publish.done', 0, 0, true);
  }

  /**
   * Handle a print error
   */
  protected handlePrintError(): void {
    this.errorOccurred = true;

    //clear all queues
    this.readyToProcessQueue.clear();
    this.processingQueue.clear();

    this.host.reportExportError();
    this.markError().then(() => {
      //ignore
    });

    this.puppeteerErrorOccurred();
  }

  /**
   * Pause print process
   */
  protected async pausePrint(pdfExportId: number, pausePrintRequest: PausePrintRequest): Promise<GenericPolluxResult> {
    const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `reportprint/pause?pdfExportId=${pdfExportId}`);
    const data = JSON.stringify(pausePrintRequest);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const options = { headers: headers };

    //lastValueFrom/firstValueFrom replace toPromise() in RXJS (toPromise is deprecated)
    const gpr: GenericPolluxResult = await lastValueFrom(this.http.post<GenericPolluxResult>(url, data, options));
    setTimeout(() => {
      //inform puppeteer so it can stop processing
      this.host.pdfExportFinished();
    }, 300);

    return gpr;
  }

  /**
   * Resume print process
   */
  protected async resumePrint(pdfExportId: number): Promise<GenericPolluxResult> {
    const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `reportprint/resume?pdfExportId=${pdfExportId}`);
    //lastValueFrom/firstValueFrom replace toPromise() in RXJS (toPromise is deprecated)
    const gpr: GenericPolluxResult = await lastValueFrom(this.http.post<GenericPolluxResult>(url, {}));
    return gpr;
  }

  /**
   * Mark print process error
   */
  protected async markError(): Promise<GenericPolluxResult> {
    const pdfExportId = this.host.PdfExportId;
    const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `reportprint/error?pdfExportId=${pdfExportId}`);
    //lastValueFrom/firstValueFrom replace toPromise() in RXJS (toPromise is deprecated)
    const gpr: GenericPolluxResult = await lastValueFrom(this.http.post<GenericPolluxResult>(url, {}));
    return gpr;
  }

  /**
   * Raises a print event
   * @param event
   * @param message
   */
  protected raisePrintEvent(event: string, message?: string) {
    if (this.raiseCssPrintEvents) {
      this.host.raisePrintEvent(event);
    } else if (message) {
      this.puppeteerLogMessage(message);
    }
  }

  /**
   * Log message for Puppeteer if run from the function app
   * @param message
   */
  protected puppeteerLogMessage(message: string): void {
    console.log(message);
    this._window.puppeteer_log?.(message);
  }

  /**
   * Export started handler for Puppeteer if run from the function app
   * @param message
   */
  protected puppeteerExportStarted(): void {
    console.log('puppeteer_exportStarted');
    this._window.puppeteer_exportStarted?.();
  }

  /**
   * Export ended handler for Puppeteer if run from the function app
   * @param message
   */
  protected puppeteerExportEnded(): void {
    console.log('puppeteer_exportEnded');
    this._window.puppeteer_exportEnded?.();
  }

  /**
   * Error handler for Puppeteer if run from the function app
   * @param message
   */
  protected puppeteerErrorOccurred(): void {
    this._window.puppeteer_log?.('Print error occurred');
    //inform fuction, so it can stop
    this._window.puppeteer_errorOccurred?.();
  }
}
